import { Mic, MicOff } from "lucide-react";
import { type FC, type RefObject, useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";

import { InputGroupButton } from "@/components/ui/input-group";
import { Spinner } from "@/components/ui/spinner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useDictation } from "@/hooks/useDictation";
import { m } from "@recobra/i18n/messages";

import { spliceDictationTranscript } from "./DictationTranscript";

type TranscriptInsertion = {
  base: string;
  caret: number;
  final: string;
  maxLength?: number;
  original: string;
  written: string;
};

/**
 * Tracks where recognized text is being spliced into the target and drops
 * results once the surrounding value diverges from what dictation wrote, so
 * edits made while listening are never overwritten.
 */
const useTranscriptInsertion = ({
  value,
  targetRef,
  onChange,
}: {
  value: string;
  targetRef: RefObject<HTMLTextAreaElement | null>;
  onChange: (value: string) => void;
}) => {
  const insertion = useRef<TranscriptInsertion>({
    base: "",
    caret: 0,
    final: "",
    original: "",
    written: "",
  });
  const valueRef = useRef(value);
  valueRef.current = value;

  const applyTranscript = useCallback(
    ({ final, interim }: { final: string; interim: string }) => {
      const slot = insertion.current;
      if (valueRef.current !== slot.written) return false;
      slot.final = final;
      const transcript = final + interim;
      const next = transcript
        ? spliceDictationTranscript(slot.base, slot.caret, transcript, slot.maxLength)
        : { value: slot.original };
      slot.written = next.value;
      onChange(next.value);
      return true;
    },
    [onChange],
  );

  const commitTranscript = useCallback(() => {
    const slot = insertion.current;
    const final = slot.final;
    if (valueRef.current !== slot.written) return;
    slot.final = "";
    if (!final) {
      slot.written = slot.original;
      onChange(slot.original);
      return;
    }
    const next = spliceDictationTranscript(slot.base, slot.caret, final, slot.maxLength);
    slot.written = next.value;
    onChange(next.value);
    requestAnimationFrame(() => {
      targetRef.current?.focus();
      targetRef.current?.setSelectionRange(next.caret, next.caret);
    });
  }, [onChange, targetRef]);

  const begin = useCallback(() => {
    const target = targetRef.current;
    const start = target?.selectionStart ?? value.length;
    const end = target?.selectionEnd ?? start;
    const maxLength = target?.maxLength;
    insertion.current = {
      base: value.slice(0, start) + value.slice(end),
      caret: start,
      final: "",
      maxLength: maxLength !== undefined && maxLength >= 0 ? maxLength : undefined,
      original: value,
      written: value,
    };
  }, [targetRef, value]);

  return { applyTranscript, commitTranscript, begin };
};

/**
 * Microphone toggle that dictates into a textarea at the caret position.
 *
 * Renders nothing where the browser has no speech recognition, so callers can
 * place it unconditionally.
 */
export const DictationButton: FC<{
  targetRef: RefObject<HTMLTextAreaElement | null>;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}> = ({ targetRef, value, onChange, disabled = false }) => {
  const { applyTranscript, commitTranscript, begin } = useTranscriptInsertion({
    value,
    targetRef,
    onChange,
  });

  const { state, start, stop } = useDictation({
    onResult: (transcript) => {
      if (!applyTranscript(transcript)) stop();
    },
    onError: (messageKey) => toast.error(m[messageKey]()),
    onEnd: commitTranscript,
  });

  useEffect(() => {
    if (disabled && (state === "preparing" || state === "listening")) stop();
  }, [disabled, state, stop]);

  if (state === "unsupported") return null;

  const listening = state === "listening";
  const preparing = state === "preparing";
  const label = dictationLabel(listening, preparing);

  const toggle = () => {
    if (listening) {
      stop();
      return;
    }
    begin();
    void start();
  };

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <InputGroupButton
            type="button"
            size="icon-xs"
            variant={listening ? "default" : "ghost"}
            disabled={disabled || preparing}
            aria-label={label}
            onClick={toggle}
          />
        }
      >
        {preparing ? <Spinner /> : null}
        {listening ? <MicOff /> : null}
        {!preparing && !listening ? <Mic /> : null}
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
};

const dictationLabel = (listening: boolean, preparing: boolean): string => {
  if (listening) return m.dictation_stop();
  if (preparing) return m.dictation_preparing();
  return m.dictation_start();
};
